"use client";

import { useRef } from "react";
import { GradText, SectionHead, SectionShell, WipTag } from "./kit";
import { gsap, useEnterStagger, useGSAP } from "./kit/motion";

/** Shared card chrome. The `group` enables hover-run micro-demos; demos use
 *  `motion-safe:` so they never run under prefers-reduced-motion. */
function RuleCard({
  span,
  title,
  desc,
  demo,
  wip,
  onHover,
}: {
  span: string;
  title: string;
  desc: string;
  demo: React.ReactNode;
  wip?: string;
  onHover?: () => void;
}) {
  return (
    <div
      data-rule-card
      onMouseEnter={onHover}
      className={`group flex flex-col rounded-ref-lg border border-line bg-surface p-6 shadow-ref ${span}`}
    >
      <div className="flex h-[72px] items-center">{demo}</div>
      <div className="mt-4 flex items-center gap-2.5">
        <h3 className="text-[17px] font-bold tracking-[-0.01em]">{title}</h3>
        {wip && <WipTag label={wip} />}
      </div>
      <p className="mt-2 text-[14.5px] leading-[1.6] text-muted">{desc}</p>
    </div>
  );
}

const DeviceDemo = (
  <div className="flex items-end gap-3">
    <div className="h-[34px] w-[20px] rounded-[4px] border-[1.5px] border-brand-600 bg-brand-600/[0.08] motion-safe:group-hover:animate-[rule-swap_1.6s_ease-in-out_infinite]" />
    <div className="flex flex-col items-center motion-safe:group-hover:animate-[rule-swap_1.6s_ease-in-out_infinite_reverse]">
      <div className="h-[22px] w-[36px] rounded-[3px] border-[1.5px] border-ink/60 bg-ink/[0.04]" />
      <div className="h-[3px] w-[44px] rounded-full bg-ink/25" />
    </div>
  </div>
);

const GeoDemo = (
  <div className="relative flex w-[120px] flex-col gap-[7px]">
    <div className="h-[5px] w-full rounded-full bg-ink/[0.08]" />
    <div className="h-[5px] w-3/4 rounded-full bg-ink/[0.08]" />
    <div className="h-[5px] w-5/6 rounded-full bg-ink/[0.08]" />
    <div className="absolute -top-2 left-[58%] flex flex-col items-center opacity-0 motion-safe:group-hover:animate-[rule-drop_1.8s_ease-out_infinite] group-hover:opacity-100">
      <span className="h-3 w-3 rounded-full bg-brand-600 shadow-[0_0_10px_var(--color-brand-500)]" />
      <span className="h-[10px] w-px bg-brand-600" />
    </div>
  </div>
);

const TimeDemo = (
  <div className="relative grid h-[52px] w-[52px] place-items-center rounded-full border-[1.5px] border-ink/30">
    <span className="absolute bottom-1/2 left-1/2 h-[17px] w-[2px] origin-bottom -translate-x-1/2 rounded-full bg-brand-600 motion-safe:group-hover:animate-[rule-sweep_1.4s_linear_infinite]" />
    <span className="absolute h-[5px] w-[5px] rounded-full bg-ink/50" />
  </div>
);

const AbDemo = (
  <div className="flex w-[150px] flex-col gap-2 font-jetbrains text-[11px]">
    <div className="flex items-center gap-2">
      <span className="w-3 text-muted">A</span>
      <div className="h-[10px] w-1/2 origin-left rounded-full bg-brand-600 motion-safe:group-hover:animate-[rule-split_1.8s_ease-in-out_infinite]" />
    </div>
    <div className="flex items-center gap-2">
      <span className="w-3 text-muted">B</span>
      <div className="h-[10px] w-[38%] rounded-full bg-data-blue" />
    </div>
  </div>
);

export default function RuleTypesSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLSpanElement>(null);
  const scrambling = useRef(false);

  useEnterStagger(rootRef, "[data-rule-card]", { y: 30, stagger: 0.09 });

  // Language card hover: scramble through greetings once per hover.
  const { contextSafe } = useGSAP({ scope: rootRef });
  const onLangHover = contextSafe(() => {
    const el = langRef.current;
    if (!el || scrambling.current) return;
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
    scrambling.current = true;
    const tl = gsap.timeline({
      onComplete: () => {
        scrambling.current = false;
      },
    });
    ["Hallo", "Bonjour", "Hola", "Hello"].forEach((w) => {
      tl.to(el, {
        scrambleText: { text: w, chars: "lowerCase", speed: 0.7 },
        duration: 0.55,
      }, "+=0.12");
    });
  });

  const LangDemo = (
    <span
      ref={langRef}
      className="font-jetbrains text-[26px] font-semibold tracking-[-0.01em] text-brand-600"
    >
      Hello
    </span>
  );

  return (
    <SectionShell id="ref-rule-types">
      <SectionHead
        center
        eyebrow="Rule types"
        title={
          <>
            Five ways to <GradText>pick a destination.</GradText>
          </>
        }
        lead="On Premium, stack rules in any order — the first match wins, and the required default catches everything else. Combine them for routes as specific as you need."
      />

      <div data-grid-block>
        <div ref={rootRef} className="grid gap-4 md:grid-cols-6">
          <RuleCard
            span="md:col-span-2"
            title="Device"
            desc="iOS, Android, tablet or desktop — send each visitor to their native home: App Store, Play Store, or the web."
            demo={DeviceDemo}
          />
          <RuleCard
            span="md:col-span-2"
            title="Geography"
            desc="Country-level routing for localized landing pages, regional stores and compliance boundaries."
            demo={GeoDemo}
          />
          <RuleCard
            span="md:col-span-2"
            title="Language"
            desc="Match the browser's Accept-Language and greet every visitor in their own words."
            demo={LangDemo}
            onHover={onLangHover}
          />
          <RuleCard
            span="md:col-span-3"
            title="Time window"
            desc="Weekend promo, office hours, late-night offers — weekly time windows flip destinations on schedule, no redeploys."
            demo={TimeDemo}
          />
          <RuleCard
            span="md:col-span-3"
            title="A/B split"
            desc="Split traffic 50/50 — or 90/10 — across destinations by weight, compare results, and shift traffic to the winner."
            demo={AbDemo}
          />
        </div>
      </div>
    </SectionShell>
  );
}
